import React from 'react';
import { keyframes } from 'styled-components';

import { anelDeFoco, styledDs as styled } from '../../../styles/theme';

export type DsButtonVariant = 'primary' | 'secondary' | 'danger';

export type DsButtonProps = {
  variant?: DsButtonVariant;
  block?: boolean;
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  flex: none;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: ${spin} 0.7s linear infinite;
`;

const Base = styled.button<{ $variant: DsButtonVariant; $block: boolean }>`
  display: ${({ $block }) => ($block ? 'flex' : 'inline-flex')};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  align-items: center;
  justify-content: center;
  gap: 7px;
  /* Cápsula, como o guia manda para todo botão. */
  height: 44px;
  padding: 0 ${({ theme }) => theme.space.xl};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeMd};
  font-weight: ${({ theme }) => theme.weight.bold};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.rapido} ${({ theme }) => theme.motion.easing},
    border-color ${({ theme }) => theme.motion.rapido} ${({ theme }) => theme.motion.easing},
    color ${({ theme }) => theme.motion.rapido} ${({ theme }) => theme.motion.easing};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    ${anelDeFoco()}
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${({ $variant, theme }) =>
    $variant === 'primary' &&
    `
    background: ${theme.colors.accent};
    border-color: ${theme.colors.accent};
    color: ${theme.colors.onAccent};

    &:hover:not(:disabled) {
      border-color: ${theme.colors.accent};
      color: ${theme.colors.onAccent};
      filter: brightness(0.95);
    }
  `}

  ${({ $variant, theme }) =>
    $variant === 'danger' &&
    `
    background: ${theme.colors.statusCritical};
    border-color: ${theme.colors.statusCritical};
    color: ${theme.colors.onAccent};

    &:hover:not(:disabled) {
      border-color: ${theme.colors.statusCritical};
      color: ${theme.colors.onAccent};
      filter: brightness(0.95);
    }
  `}
`;

export const DsButton: React.FC<DsButtonProps> = ({
  variant = 'primary',
  block = false,
  loading = false,
  disabled,
  type = 'button',
  children,
  ...rest
}) => (
  <Base $variant={variant} $block={block} type={type} disabled={disabled || loading} {...rest}>
    {loading && <Spinner aria-hidden="true" />}
    {children}
  </Base>
);
